import React, { useRef } from 'react';
import { array, bool, string } from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { NamedLink, Carousel } from '..';
import Flag from 'react-world-flags';
import AmsiLogo from '../SectionPartners/logos/logo-amsi.png';

import css from './SectionLocal.module.css';

const CheckIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" aria-hidden="true" className={css.checkIcon}>
    <circle cx="8" cy="8" r="8" fill="rgba(255,255,255,0.25)" />
    <path
      d="M4.5 8.2l2.3 2.3 4.7-4.7"
      stroke="#fff"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const disciplineLabel = key =>
  (key || '').replace(/([A-Z])/g, ' $1').replace(/^./, character => character.toUpperCase());

const ASSOCIATION_NETWORKS = [
  // Switzerland
  {
    key: 'sbv-asgm',
    name: 'Swiss Mountain Guides',
    acronym: 'ASGM',
    country: 'CH',
    categoryId: 'SectionLocal.associationCategoryMountainGuide',
    url: 'https://sbv-asgm.ch/',
  },
  {
    key: 'guide-alpine-ticino',
    name: 'Guide Alpine Ticino',
    acronym: 'GAT',
    country: 'CH',
    categoryId: 'SectionLocal.associationCategoryMountainGuide',
    url: 'https://www.guidealpineticino.ch/',
  },
  {
    key: 'swiss-snowsports',
    name: 'Swiss Snowsports',
    acronym: 'SSSA',
    country: 'CH',
    categoryId: 'SectionLocal.associationCategorySkiInstructor',
    url: 'https://www.snowsports.ch/',
  },
  {
    key: 'swiss-outdoor-association',
    name: 'Swiss Outdoor Association',
    acronym: 'SOA',
    country: 'CH',
    categoryId: 'SectionLocal.associationCategoryRafting',
    url: 'https://www.swissoutdoorassociation.ch/',
  },
  {
    key: 'shv-fsvl',
    name: 'Swiss Hang Gliding and Paragliding Association',
    acronym: 'SHV',
    country: 'CH',
    categoryId: 'SectionLocal.associationCategoryParagliding',
    url: 'https://www.shv-fsvl.ch/',
  },
  {
    key: 'asam-swl',
    name: 'ASAM-SWL',
    acronym: 'ASAM',
    country: 'CH',
    categoryId: 'SectionLocal.associationCategoryMountainLeader',
    url: 'https://asam-swl.ch/',
  },
  {
    key: 'swiss-cycling-guide',
    name: 'Swiss Cycling Guide',
    acronym: 'SCG',
    country: 'CH',
    categoryId: 'SectionLocal.associationCategoryBikeGuide',
    url: 'https://www.swiss-cycling-guide.ch/',
  },

  // Italy
  {
    key: 'guide-alpine-italia',
    name: 'Guide Alpine Italiane',
    acronym: 'GAI',
    country: 'IT',
    categoryId: 'SectionLocal.associationCategoryMountainGuide',
    url: 'https://www.guidealpine.it/',
  },
  {
    key: 'amsi',
    name: 'AMSI',
    acronym: 'AMSI',
    country: 'IT',
    logo: AmsiLogo,
    logoAlt: 'Associazione Maestri di Sci Italiani',
    categoryId: 'SectionLocal.associationCategorySkiInstructor',
    url: 'https://amsi.it/',
  },
  {
    key: 'fasi',
    name: 'Federazione Arrampicata Sportiva Italiana',
    acronym: 'FASI',
    country: 'IT',
    categoryId: 'SectionLocal.associationCategoryClimbing',
    url: 'https://www.federclimb.it/',
  },
  {
    key: 'fivl',
    name: 'Federazione Italiana Volo Libero',
    acronym: 'FIVL',
    country: 'IT',
    categoryId: 'SectionLocal.associationCategoryParagliding',
    url: 'https://www.fivl.it/',
  },
  {
    key: 'firaft',
    name: 'Federazione Italiana Rafting',
    acronym: 'FIRaft',
    country: 'IT',
    categoryId: 'SectionLocal.associationCategoryRafting',
    url: 'https://www.federrafting.it/',
  },
  {
    key: 'aigae',
    name: 'AIGAE',
    acronym: 'AIGAE',
    country: 'IT',
    categoryId: 'SectionLocal.associationCategoryMountainLeader',
    url: 'https://www.aigae.org/',
  },

  // France
  {
    key: 'sngm',
    name: 'SNGM',
    acronym: 'SNGM',
    country: 'FR',
    categoryId: 'SectionLocal.associationCategoryMountainGuide',
    url: 'https://www.sngm.com/',
  },
  {
    key: 'snmsf',
    name: 'Ecole du Ski Francais',
    acronym: 'ESF',
    country: 'FR',
    categoryId: 'SectionLocal.associationCategorySkiInstructor',
    url: 'https://www.esf.net/',
  },
  {
    key: 'ffme',
    name: 'Federation Francaise de la Montagne et de l Escalade',
    acronym: 'FFME',
    country: 'FR',
    categoryId: 'SectionLocal.associationCategoryClimbing',
    url: 'https://www.ffme.fr/',
  },
  {
    key: 'ffvl',
    name: 'Federation Francaise de Vol Libre',
    acronym: 'FFVL',
    country: 'FR',
    categoryId: 'SectionLocal.associationCategoryParagliding',
    url: 'https://federation.ffvl.fr/',
  },
  {
    key: 'mcf',
    name: 'Moniteur Cycliste Francais',
    acronym: 'MCF',
    country: 'FR',
    categoryId: 'SectionLocal.associationCategoryBikeGuide',
    url: 'https://www.moniteurcycliste.com/',
  },

  // European umbrella bodies
  {
    key: 'ifmga',
    name: 'IFMGA / UIAGM / IVBV',
    acronym: 'IFMGA',
    categoryId: 'SectionLocal.associationCategoryMountainGuide',
    url: 'https://ifmga.net/member-associations/member-associations',
  },
  {
    key: 'uimla',
    name: 'UIMLA',
    acronym: 'UIMLA',
    categoryId: 'SectionLocal.associationCategoryMountainLeader',
    url: 'https://uimla.org/members/',
  },
  {
    key: 'isia',
    name: 'ISIA',
    acronym: 'ISIA',
    categoryId: 'SectionLocal.associationCategorySkiInstructor',
    url: 'https://isia.ski/members/',
  },
  {
    key: 'eomtbing',
    name: 'EO-MTBing',
    acronym: 'EO MTB',
    categoryId: 'SectionLocal.associationCategoryBikeGuide',
    url: 'https://europeanbikeguides.com/',
  },
  {
    key: 'fai-civl',
    name: 'FAI CIVL',
    acronym: 'CIVL',
    categoryId: 'SectionLocal.associationCategoryParagliding',
    url: 'https://www.fai.org/commission/civl',
  },
  {
    key: 'irf-gte',
    name: 'International Rafting Federation',
    acronym: 'IRF',
    categoryId: 'SectionLocal.associationCategoryRafting',
    url: 'https://internationalrafting.com/guiding/',
  },
  {
    key: 'rescue3-europe',
    name: 'Rescue 3 Europe',
    acronym: 'R3E',
    categoryId: 'SectionLocal.associationCategoryRescue',
    url: 'https://www.rescue3europe.com/',
  },
  {
    key: 'appi',
    name: 'APPI Paragliding',
    acronym: 'APPI',
    categoryId: 'SectionLocal.associationCategoryParagliding',
    url: 'https://flyappi.org/',
  },

  // Austria, Germany, Spain, Slovenia and Nordics
  {
    key: 'voebs',
    name: 'VOBS',
    acronym: 'VOBS',
    country: 'AT',
    categoryId: 'SectionLocal.associationCategoryMountainGuide',
    url: 'https://www.bergfuehrer.at/',
  },
  {
    key: 'austrian-ski-schools',
    name: 'Snowsport Austria',
    acronym: 'OSSV',
    country: 'AT',
    categoryId: 'SectionLocal.associationCategorySkiInstructor',
    url: 'https://www.snowsportaustria.at/',
  },
  {
    key: 'vdbs',
    name: 'VDBS',
    acronym: 'VDBS',
    country: 'DE',
    categoryId: 'SectionLocal.associationCategoryMountainGuide',
    url: 'https://www.vdbs.de/',
  },
  {
    key: 'dslv',
    name: 'DSLV',
    acronym: 'DSLV',
    country: 'DE',
    categoryId: 'SectionLocal.associationCategorySkiInstructor',
    url: 'https://www.dslv.org/',
  },
  {
    key: 'dimb',
    name: 'DIMB',
    acronym: 'DIMB',
    country: 'DE',
    categoryId: 'SectionLocal.associationCategoryBikeGuide',
    url: 'https://www.dimb.de/',
  },
  {
    key: 'dhv',
    name: 'Deutscher Haengegleiterverband',
    acronym: 'DHV',
    country: 'DE',
    categoryId: 'SectionLocal.associationCategoryParagliding',
    url: 'https://www.dhv.de/',
  },
  {
    key: 'aegm',
    name: 'AEGM',
    acronym: 'AEGM',
    country: 'ES',
    categoryId: 'SectionLocal.associationCategoryMountainGuide',
    url: 'https://www.aegm.org/',
  },
  {
    key: 'aepedi',
    name: 'AEPEDI',
    acronym: 'AEPEDI',
    country: 'ES',
    categoryId: 'SectionLocal.associationCategorySkiInstructor',
    url: 'https://aepedi.org/',
  },
  {
    key: 'zgvs',
    name: 'ZGVS',
    acronym: 'ZGVS',
    country: 'SI',
    categoryId: 'SectionLocal.associationCategoryMountainGuide',
    url: 'https://www.zgvs.si/',
  },
  {
    key: 'zuts',
    name: 'ZUTS Slovenia',
    acronym: 'ZUTS',
    country: 'SI',
    categoryId: 'SectionLocal.associationCategorySkiInstructor',
    url: 'https://www.zuts.si/',
  },
  {
    key: 'nortind',
    name: 'Nortind',
    acronym: 'NT',
    country: 'NO',
    categoryId: 'SectionLocal.associationCategoryMountainGuide',
    url: 'https://www.nortind.no/',
  },
  {
    key: 'den-norske-skiskole',
    name: 'Den Norske Skiskole',
    acronym: 'DNS',
    country: 'NO',
    categoryId: 'SectionLocal.associationCategorySkiInstructor',
    url: 'https://isia.ski/members/',
  },
  {
    key: 'sbo',
    name: 'SBO Sweden',
    acronym: 'SBO',
    country: 'SE',
    categoryId: 'SectionLocal.associationCategoryMountainGuide',
    url: 'https://www.sbo.nu/',
  },
  {
    key: 'sve-skidlarare',
    name: 'Svenska Skidlararforeningen',
    acronym: 'SSF',
    country: 'SE',
    categoryId: 'SectionLocal.associationCategorySkiInstructor',
    url: 'https://isia.ski/members/',
  },
  {
    key: 'ski-instructors-finland',
    name: 'Finnish National Association of Ski Instructors',
    acronym: 'SHO',
    country: 'FI',
    categoryId: 'SectionLocal.associationCategorySkiInstructor',
    url: 'https://isia.ski/members/',
  },
];

const SectionLocal = props => {
  const { rootClassName, className, providers: propProviders, isAuthenticated } = props;
  const intl = useIntl();
  const sliderRef = useRef();
  const providers = Array.isArray(propProviders) ? propProviders.filter(provider => provider?.key) : [];
  const classes = classNames(rootClassName || css.root, className);
  const providerCtaPage = isAuthenticated ? 'NewListingPage' : 'SignupPage';
  const providerCtaTo = isAuthenticated
    ? {}
    : { search: '?intent=provider&source=home-provider-section' };

  if (providers.length === 0) return null;

  const handlePrev = () => {
    if (sliderRef.current) sliderRef.current.slickPrev();
  };

  const handleNext = () => {
    if (sliderRef.current) sliderRef.current.slickNext();
  };

  return (
    <div className={classes}>
      <div className={css.content}>
        <div className={css.textColumn}>
          <span className={css.eyebrow}>
            <FormattedMessage id="SectionLocal.eyebrow" defaultMessage="Local professionals" />
          </span>
          <h2 className={css.title}>
            <FormattedMessage id="SectionLocal.title" />
          </h2>
          <p className={css.description}>
            <FormattedMessage id="SectionLocal.cardContent" />
          </p>
          <div className={css.actionRow}>
            <NamedLink name="SearchPage" className={css.primaryLink}>
              <FormattedMessage id="General.browse" defaultMessage="Explore activities" />
            </NamedLink>
            <NamedLink name={providerCtaPage} to={providerCtaTo} className={css.secondaryLink}>
              <FormattedMessage id="SectionLocal.providerCtaButton" defaultMessage="Add your activity" />
            </NamedLink>
          </div>
          <div className={css.providerNote}>
            <span className={css.providerNoteTitle}>
              <FormattedMessage
                id="SectionLocal.providerNoteTitle"
                defaultMessage="Are you a guide, school, or outdoor company?"
              />
            </span>
            <p>
              <FormattedMessage
                id="SectionLocal.providerNoteText"
                defaultMessage="Create a provider profile and publish your first activity on Crion."
              />
            </p>
          </div>
          <div className={css.associationPanel}>
            <span className={css.associationTitle}>
              <FormattedMessage
                id="SectionLocal.associationTitle"
                defaultMessage="Professional associations and certification networks"
              />
            </span>
            <div className={css.associationSlider}>
              {ASSOCIATION_NETWORKS.map(association => (
                <a
                  key={association.key}
                  href={association.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={css.associationCard}
                  aria-label={association.name}
                >
                  <span className={css.associationMark}>
                    {association.logo ? (
                      <img
                        src={association.logo}
                        alt={association.logoAlt || association.name}
                        className={css.associationLogo}
                      />
                    ) : (
                      <span className={css.associationAcronym}>{association.acronym}</span>
                    )}
                  </span>
                  <span className={css.associationCopy}>
                    <strong>{association.name}</strong>
                    <small>
                      <FormattedMessage id={association.categoryId} />
                      {association.country ? (
                        <span className={css.associationCountry}>
                          <Flag code={association.country} alt={association.country} />
                          <span>{association.country}</span>
                        </span>
                      ) : null}
                    </small>
                  </span>
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className={css.sliderColumn}>
          <div className={css.sliderHeader}>
            <span className={css.sliderLabel}>
              <FormattedMessage id="SectionLocal.sliderTitle" defaultMessage="Guides and providers on Crion" />
            </span>
            <div className={css.sliderArrows}>
              <button type="button" className={css.arrowBtn} onClick={handlePrev} aria-label="Previous">
                &#8249;
              </button>
              <button type="button" className={css.arrowBtn} onClick={handleNext} aria-label="Next">
                &#8250;
              </button>
            </div>
          </div>

          <Carousel
            ref={sliderRef}
            columns={2}
            responsive={{ 760: { columns: 2 }, 1023: { columns: 3 }, 9999: { columns: 2 } }}
            showArrows={false}
            contentClassName={css.carouselContent}
          >
            {providers.map(provider => {
              const firstName = (provider.name || '').split(' ')[0];
              const disciplines = Array.isArray(provider.disciplines) ? provider.disciplines : [];
              const visibleDisciplines = disciplines.slice(0, 2);
              const hiddenDisciplineCount = Math.max(disciplines.length - visibleDisciplines.length, 0);

              return (
                <div key={provider.key} className={css.cardSlot}>
                  <NamedLink name="ProfilePage" params={{ id: provider.key }} className={css.cardLink}>
                    <div
                      className={css.providerCard}
                      style={!provider.profileImage ? { background: provider.color || '#1a3028' } : undefined}
                    >
                      {provider.profileImage ? (
                        <img src={provider.profileImage} alt={firstName} className={css.providerCardImage} />
                      ) : (
                        <div className={css.providerCardPlaceholder} aria-hidden="true">
                          {provider.initials || firstName.slice(0, 2).toUpperCase()}
                        </div>
                      )}
                      <div className={css.providerCardBody}>
                        <span className={css.providerName}>{firstName}</span>
                        {provider.location ? (
                          <span className={css.providerLocation}>{provider.location}</span>
                        ) : null}
                        {visibleDisciplines.length > 0 ? (
                          <div className={css.disciplines}>
                            {visibleDisciplines.map(discipline => (
                              <span key={discipline} className={css.disciplineTag}>
                                <CheckIcon />
                                {intl.formatMessage({
                                  id: `General.${discipline}`,
                                  defaultMessage: disciplineLabel(discipline),
                                })}
                              </span>
                            ))}
                            {hiddenDisciplineCount > 0 ? (
                              <span className={classNames(css.disciplineTag, css.moreDisciplineTag)}>
                                +{hiddenDisciplineCount}
                              </span>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </NamedLink>
                </div>
              );
            })}
          </Carousel>
        </div>
      </div>
    </div>
  );
};

SectionLocal.defaultProps = {
  rootClassName: null,
  className: null,
  providers: null,
  isAuthenticated: false,
};

SectionLocal.propTypes = {
  rootClassName: string,
  className: string,
  providers: array,
  isAuthenticated: bool,
};

export default SectionLocal;
